import React from "react";
import { useNavigate } from "react-router-dom";
import { useTasks } from "../../context/TaskContext";
import { useAuth } from "../../context/AuthContext";
import KanbanBoard from "../../components/common/KanbanBoard";
import { formatDate } from "../../utils/dateUtils";

const TaskKanbanView = ({ tasks }) => {
  const navigate = useNavigate();
  const { updateTask } = useTasks();
  const { checkAccess } = useAuth();

  const COLUMNS = [
    { id: "Pending", title: "To Do", dotColor: "bg-red-500" },
    { id: "In Progress", title: "In Progress", dotColor: "bg-amber-500" },
    { id: "Completed", title: "Completed", dotColor: "bg-emerald-500" }
  ];

  const handleDragEnd = (result) => {
    const { destination, draggableId } = result;
    if (!destination) return;
    if (checkAccess('task.edit')) {
      updateTask(draggableId, { status: destination.droppableId });
    }
  };

  const renderCard = (task) => {
    const assigneeName = (task.assignedName && !task.assignedName.startsWith("Employee #") ? task.assignedName : null)
      || (task.assignedTo && !task.assignedTo.startsWith("Employee #") ? task.assignedTo : null);

    return (
      <div 
        onClick={() => { if(checkAccess('task.view')) navigate(`/admin/task/view/${task.id}`) }}
        className="bg-white dark:bg-[#161b22] p-4 rounded-xl shadow-xs border border-gray-200 dark:border-[#30363d] hover:shadow-md dark:hover:border-slate-600 transition cursor-grab active:cursor-grabbing flex flex-col min-h-[145px]"
      >
        <div className="flex justify-between items-start gap-2">
          <div className="overflow-hidden">
            <h4 className="font-bold text-gray-900 dark:text-white text-sm leading-tight truncate">{task.title}</h4>
            <p className="text-[11px] text-blue-600 dark:text-blue-400 font-semibold mt-1 truncate flex items-center gap-1">
              📁 {task.projectName || "General"}
            </p>
          </div>
          <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded shrink-0 ${task.priority === 'High' || task.priority === 'Urgent' ? 'bg-red-50 dark:bg-rose-950/40 text-red-600 dark:text-rose-400 border border-red-200 dark:border-rose-800/60' : 'bg-gray-50 dark:bg-slate-800 text-gray-600 dark:text-slate-300 border border-gray-200 dark:border-slate-700'}`}>
            {task.priority || 'Medium'}
          </span>
        </div>

        {/* Assignee & Progress */}
        <div className="mt-3 flex items-center justify-between">
          {assigneeName ? (
            <div className="flex items-center gap-1.5" title={`Assigned to: ${assigneeName}`}>
              <div className="w-5 h-5 rounded-full bg-blue-100 dark:bg-blue-900/60 text-blue-700 dark:text-blue-300 font-bold text-[10px] flex items-center justify-center shrink-0 border border-blue-200 dark:border-blue-800">
                {assigneeName.charAt(0).toUpperCase()}
              </div>
              <span className="text-[11px] font-medium text-gray-700 dark:text-slate-300 truncate max-w-[130px]">
                {assigneeName}
              </span>
            </div>
          ) : (
            <span className="text-[10px] text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 px-1.5 py-0.5 rounded font-medium border border-amber-200 dark:border-amber-900/40">
              Unassigned
            </span>
          )}

          {task.progress !== undefined && task.progress > 0 && (
            <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
              {task.progress}%
            </span>
          )}
        </div>

        <div className="mt-auto pt-2.5 border-t border-gray-100 dark:border-[#30363d] flex justify-between items-center text-[11px]">
           <span className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider">Due</span>
           <span className="font-semibold text-gray-700 dark:text-slate-300">{formatDate(task.dueDate || task.due_date)}</span>
        </div>
      </div>
    );
  };

  return (
    <KanbanBoard 
      columns={COLUMNS}
      items={tasks || []}
      groupBy="status"
      onDragEnd={handleDragEnd}
      renderCard={renderCard}
    />
  );
};

export default TaskKanbanView;